import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TopBar } from "../components/TopBar";
import { OrgForm } from "../components/OrgForm";
import { Card, ErrorText, InviteLinkBox, Section, btnPrimary } from "../components/ui";
import { api, ApiError } from "../lib/api";
import { inviteUrlFrom } from "../lib/session";
import { isInContract, type Invitation, type Org, type OrgInput } from "../lib/types";

export function OpsOrgPage() {
  const { t } = useTranslation(["common", "admin"]);
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const [saved, setSaved] = useState(false);
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);

  const org = useQuery({
    queryKey: ["ops-org", id],
    queryFn: () => api<Org>(`/api/system-admin/orgs/${encodeURIComponent(id)}`),
    enabled: !!id,
    // 開くたびに最新を読み、入力の途中では読み直さない（値が戻らないように）
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });

  const save = useMutation({
    mutationFn: (v: OrgInput) => api<Org>(`/api/system-admin/orgs/${encodeURIComponent(id)}`, { method: "PATCH", body: v }),
    onMutate: () => setSaved(false),
    onSuccess: (data) => {
      setSaved(true);
      if (data && typeof data === "object" && "id" in data) queryClient.setQueryData(["ops-org", id], data);
      // 返事に学校が無い時も、次に開いた時は読み直す（gcTime 0）
      void queryClient.invalidateQueries({ queryKey: ["ops-orgs"] });
    },
  });

  const invite = useMutation({
    mutationFn: () => api<Invitation>(`/api/system-admin/orgs/${encodeURIComponent(id)}/invitations`, { method: "POST", body: {} }),
    onSuccess: (data) => setInviteUrl(inviteUrlFrom(data)),
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <TopBar subtitle={org.data?.name} />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link to="/ops" className="text-sm text-muted underline">{t("admin:ops_back")}</Link>

        {org.isLoading && <p className="text-muted mt-4">{t("common:loading")}</p>}
        {org.error && (
          <div className="mt-4">
            <ErrorText>{org.error instanceof ApiError ? org.error.message : t("common:error_generic")}</ErrorText>
          </div>
        )}

        {org.data && (
          <>
            <div className="mt-4 flex items-end justify-between gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-primary">{org.data.name}</h1>
              <div className="text-sm text-muted">
                {t("admin:org_learners")} {org.data.learner_count ?? 0} / {org.data.learner_cap ?? t("admin:org_no_cap")}
                {!isInContract(org.data) && <span className="ml-3 text-accent">{t("admin:org_out_of_contract")}</span>}
              </div>
            </div>

            <Section title={t("admin:ops_org_title")}>
              <Card className="p-6">
                {/* 読み込み後に一度だけ初期値を入れる。保存後に作り直さない */}
                <OrgForm
                  initial={org.data}
                  submitting={save.isPending}
                  submitLabel={t("common:save")}
                  onSubmit={(v) => save.mutate(v)}
                />
                {saved && <p className="mt-3 text-sm text-green-700">{t("common:saved")}</p>}
                {save.error && (
                  <div className="mt-3">
                    <ErrorText>{save.error instanceof ApiError ? save.error.message : t("common:error_generic")}</ErrorText>
                  </div>
                )}
              </Card>
            </Section>

            <Section title={t("common:role_org_admin")}>
              <Card className="p-6">
                <p className="text-sm text-muted mb-3">{t("admin:ops_invite_note")}</p>
                <button data-testid="org-invite-admin" type="button" disabled={invite.isPending}
                  onClick={() => invite.mutate()} className={btnPrimary}>
                  {t("admin:ops_invite_admin")}
                </button>
                {inviteUrl && <InviteLinkBox url={inviteUrl} />}
                {invite.error && (
                  <div className="mt-3">
                    <ErrorText>{invite.error instanceof ApiError ? invite.error.message : t("common:error_generic")}</ErrorText>
                  </div>
                )}
              </Card>
            </Section>
          </>
        )}
      </main>
    </div>
  );
}
